import { useTranslation } from 'react-i18next';

import { useStripe } from '@stripe/react-stripe-js';
import { gql, useSubscription } from '@apollo/client';
import { NOTIFICATION_TYPE, showNotification } from '@thinknet-gmbh/platform-common-ui';
import { StripePaymentProcessStatusesEnum } from '@thinknet-gmbh/platform-common';

export const SUBSCRIBE_TO_PAYMENT_CHANGES = gql`
  subscription subscribeToPaymentChanges {
    subscribeToPaymentChanges {
      accountId
      status
      clientSecret
    }
  }
`;

const PAYMENT_FAILED_STATUSES = [
  StripePaymentProcessStatusesEnum.CHARGE_FAILED,
  StripePaymentProcessStatusesEnum.PAYMENT_INTENT_FAILED,
];
const PAYMENT_SUCCESS_STATUSES = [
  StripePaymentProcessStatusesEnum.CHARGE_SUCCEEDED,
  StripePaymentProcessStatusesEnum.PAYMENT_INTENT_SUCCEEDED,
];
const PAYMENT_PENDING_STATUSES = [
  StripePaymentProcessStatusesEnum.CHARGE_PENDING,
  StripePaymentProcessStatusesEnum.PAYMENT_INTENT_PROCESSING,
  StripePaymentProcessStatusesEnum.PAYMENT_INTENT_REQUIRES_ACTIONS,
];

export const useHandlePaymentChanges = ({
  onPaymentFinished,
}: {
  onPaymentFinished: () => void;
}) => {
  const { t } = useTranslation('dashboard');
  const stripe = useStripe();

  const { data, loading } = useSubscription(SUBSCRIBE_TO_PAYMENT_CHANGES);

  const payment = data?.subscribeToPaymentChanges || null;

  if (!loading && payment) {
    const status = payment.status;
    let isShowLoading = true;

    if (PAYMENT_SUCCESS_STATUSES.includes(status)) {
      isShowLoading = false;
      showNotification({
        type: NOTIFICATION_TYPE.success,
        message: t('notifications.successSubscription'),
      });
      onPaymentFinished();
    }

    if (PAYMENT_FAILED_STATUSES.includes(status)) {
      isShowLoading = false;
      showNotification({
        type: NOTIFICATION_TYPE.error,
        message: t('notifications.paymentFailed'),
      });
      onPaymentFinished();
    }

    if (PAYMENT_PENDING_STATUSES.includes(status)) {
      isShowLoading = true;

      if (status === StripePaymentProcessStatusesEnum.PAYMENT_INTENT_REQUIRES_ACTIONS) {
        try {
          stripe?.confirmCardPayment(payment.clientSecret).then(result => {
            if (result.error) {
              showNotification({
                type: NOTIFICATION_TYPE.error,
                message: t('notifications.paymentVerificationFailed'),
              });
              onPaymentFinished();
            }
          });
        } catch {
          onPaymentFinished();
        }
      }
    }

    return { isShowLoading };
  }

  return { isShowLoading: false };
};
